import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { OrderData, StageData, DepartmentData, OperationData, getGroupColor, getGroupBadgeColor } from "@/data/singleLaneGanttData";
import { DueDateMarker } from "./DueDateMarker";

interface SingleLaneGanttProps {
  orders: OrderData[];
  startDate: Date;
  days: number;
  onTimeUpdate?: (level: string, id: string, newStart: string, newEnd: string) => void;
  onDueMarkerClick?: (orderId: string) => void;
}

interface ExpandedState {
  orders: Set<string>;
  stages: Set<string>;
  departments: Set<string>;
}

interface GanttBarProps {
  startDate: string;
  endDate: string;
  timelineStart: Date;
  days: number;
  label: string;
  progress?: number;
  group?: number;
  level: 'order' | 'stage' | 'department' | 'operation';
  isGhost?: boolean;
  tooltipContent?: React.ReactNode;
  className?: string;
}

// 單一車道甘特條組件
function GanttBar({
  startDate,
  endDate,
  timelineStart,
  days,
  label,
  progress,
  group,
  level,
  isGhost = false,
  tooltipContent,
  className
}: GanttBarProps) {
  // 計算位置
  const dayDiff = (start: Date, end: Date) => 
    Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  const startIdx = Math.max(0, dayDiff(timelineStart, new Date(startDate)));
  const endIdx = Math.min(days, dayDiff(timelineStart, new Date(endDate)) + 1);

  if (startIdx >= days || endIdx <= 0 || startIdx >= endIdx) {
    return null;
  }

  // 根據層級和並行組決定樣式
  const getBarStyle = () => {
    if (isGhost) {
      return 'bg-muted/30 border border-muted-foreground/20';
    }

    const baseStyles = {
      order: 'bg-primary text-primary-foreground',
      stage: 'bg-secondary text-secondary-foreground',
      department: 'bg-accent text-accent-foreground',
      operation: 'bg-muted text-muted-foreground'
    };

    let style = baseStyles[level];
    
    if (group && level !== 'order') {
      const groupStyle = getGroupColor(group);
      style = groupStyle;
    }

    if (progress !== undefined && progress === 0) {
      style += ' opacity-40';
    }

    return style;
  };

  const barElement = (
    <div
      className={cn(
        "rounded flex items-center justify-center cursor-pointer transition-all duration-200 relative border-2",
        getBarStyle(),
        className
      )}
      style={{
        gridColumn: `${startIdx + 1} / ${endIdx + 1}`,
        height: '80%',
        alignSelf: 'center',
        minWidth: '24px'
      }}
    >
      {/* 並行組徽章 */}
      {group && level !== 'order' && !isGhost && (
        <div className={cn(
          "absolute -top-2 -left-1 text-xs px-1.5 py-0.5 rounded font-bold",
          getGroupBadgeColor(group)
        )}>
          G{group}
        </div>
      )}

      {/* 標籤 */}
      <span className="text-xs font-medium truncate px-2">
        {progress !== undefined ? `${label} ${Math.round(progress * 100)}%` : label}
      </span>
      
      {/* 進度條 */}
      {progress !== undefined && progress > 0 && (
        <div 
          className="absolute bottom-0 left-0 bg-white/30 rounded-b"
          style={{ 
            width: `${progress * 100}%`, 
            height: '3px' 
          }}
        />
      )}
    </div>
  );

  if (tooltipContent) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {barElement}
          </TooltipTrigger>
          <TooltipContent>
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return barElement;
}

// 階層式列組件
interface HierarchyRowProps {
  level: number;
  children: React.ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
  timelineContent: React.ReactNode;
  days: number;
}

function HierarchyRow({ level, children, isExpanded, onToggle, timelineContent, days }: HierarchyRowProps) {
  return (
    <div style={{ display: 'contents' }}>
      {/* 左側標籤區域 */}
      <div
        className="border-r border-b px-4 flex items-center overflow-hidden hover:bg-muted/10"
        style={{ 
          paddingLeft: `${16 + level * 20}px`,
          minHeight: '64px'
        }}
      >
        <div className="flex items-center gap-2 flex-1">
          {/* 連接線 */}
          {level > 0 && (
            <div className="w-4 h-0.5 bg-muted-foreground/30 mr-1" />
          )}
          
          {/* 展開/收合按鈕 */}
          {onToggle && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1 hover:bg-muted/50"
              onClick={onToggle}
            >
              {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </Button>
          )}
          
          {/* 內容 */}
          <div className="flex-1">
            {children}
          </div>
        </div>
      </div>

      {/* 右側時間軸區域 */}
      <div
        className="border-b bg-muted/10 grid items-center overflow-hidden relative"
        style={{
          gridColumn: `span ${days}`,
          gridTemplateColumns: `repeat(${days}, 1fr)`,
          minHeight: '64px'
        }}
      >
        {/* 背景網格線 */}
        {Array.from({ length: days }).map((_, i) => (
          <div key={i} className={`border-r border-muted/10 ${i === days - 1 ? 'border-r-0' : ''} h-full`} />
        ))}
        
        {/* 時間軸內容 */}
        <div className="absolute inset-0 grid items-center" style={{ gridTemplateColumns: `repeat(${days}, 1fr)` }}>
          {timelineContent}
        </div>
      </div>
    </div>
  );
}

export function SingleLaneGantt({ orders, startDate, days, onTimeUpdate, onDueMarkerClick }: SingleLaneGanttProps) {
  const [expanded, setExpanded] = useState<ExpandedState>({
    orders: new Set(['ORD-001']),
    stages: new Set(['ORD-001-ST-NEW']),
    departments: new Set(['ORD-001-ST-NEW-D-SALES'])
  });

  // 麵包屑狀態
  const [breadcrumb, setBreadcrumb] = useState<{ orderId?: string; stageId?: string; deptId?: string }>({});

  const toggleExpanded = (type: keyof ExpandedState, id: string) => {
    setExpanded(prev => {
      const newSet = new Set(prev[type]);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return { ...prev, [type]: newSet };
    });
  };

  const renderOperations = (operations: OperationData[], deptId: string, stageId: string, orderId: string) => {
    if (!expanded.departments.has(`${orderId}-${stageId}-${deptId}`)) return null;

    return operations.map(operation => (
      <HierarchyRow
        key={operation.id}
        level={3}
        days={days}
        timelineContent={
          <GanttBar
            startDate={operation.start}
            endDate={operation.end}
            timelineStart={startDate}
            days={days}
            label={operation.name}
            progress={operation.progress}
            level="operation"
            tooltipContent={
              <div className="space-y-1">
                <div className="font-medium">{operation.name}</div>
                <div>負責人: {operation.assignee}</div>
                <div>進度: {Math.round(operation.progress * 100)}%</div>
                <div>開始: {new Date(operation.start).toLocaleDateString()}</div>
                <div>結束: {new Date(operation.end).toLocaleDateString()}</div>
              </div>
            }
          />
        }
      >
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
          <div>
            <div className="text-xs font-medium">{operation.name}</div>
            <div className="text-xs text-muted-foreground">
              {operation.assignee} • {Math.round(operation.progress * 100)}%
            </div>
          </div>
        </div>
      </HierarchyRow>
    ));
  };

  const renderDepartments = (departments: DepartmentData[], stageId: string, orderId: string, parentStage: StageData) => {
    if (!expanded.stages.has(`${orderId}-${stageId}`)) return null;

    return departments.map(department => {
      const deptKey = `${orderId}-${stageId}-${department.id}`;
      const isDeptExpanded = expanded.departments.has(deptKey);

      return (
        <div key={department.id} className="contents">
          {/* 部門列 */}
          <HierarchyRow
            level={2}
            days={days}
            isExpanded={isDeptExpanded}
            onToggle={() => toggleExpanded('departments', deptKey)}
            timelineContent={
              <>
                {/* Ghost 父層條 (階段) */}
                <GanttBar
                  startDate={parentStage.start}
                  endDate={parentStage.end}
                  timelineStart={startDate}
                  days={days}
                  label=""
                  level="stage"
                  isGhost
                  className="opacity-20"
                />
                {/* 部門條 */}
                <GanttBar
                  startDate={department.start}
                  endDate={department.end}
                  timelineStart={startDate}
                  days={days}
                  label={department.name}
                  progress={department.progress}
                  group={department.group}
                  level="department"
                  tooltipContent={
                    <div className="space-y-1">
                      <div className="font-medium">{department.name}</div>
                      <div>並行組: G{department.group}</div>
                      <div>進度: {Math.round(department.progress * 100)}%</div>
                      <div>工序: {department.operations.length} 個</div>
                      <div>開始: {new Date(department.start).toLocaleDateString()}</div>
                      <div>結束: {new Date(department.end).toLocaleDateString()}</div>
                    </div>
                  }
                />
              </>
            }
          >
            <div className="flex items-center gap-2">
              <Badge className={getGroupBadgeColor(department.group)}>
                G{department.group}
              </Badge>
              <div>
                <div className="text-sm font-medium">{department.name}</div>
                <div className="text-xs text-muted-foreground">
                  {department.operations.length} 個工序 • {Math.round(department.progress * 100)}%
                </div>
              </div>
            </div>
          </HierarchyRow>

          {/* 工序列 */}
          {renderOperations(department.operations, department.id, stageId, orderId)}
        </div>
      );
    });
  };

  const renderStages = (stages: StageData[], orderId: string, parentOrder: OrderData) => {
    if (!expanded.orders.has(orderId)) return null;

    return stages.map(stage => {
      const stageKey = `${orderId}-${stage.id}`;
      const isStageExpanded = expanded.stages.has(stageKey);

      return (
        <div key={stage.id} className="contents">
          {/* 階段列 */}
          <HierarchyRow
            level={1}
            days={days}
            isExpanded={isStageExpanded}
            onToggle={() => toggleExpanded('stages', stageKey)}
            timelineContent={
              <>
                {/* Ghost 父層條 (訂單) */}
                <GanttBar
                  startDate={parentOrder.start}
                  endDate={parentOrder.end}
                  timelineStart={startDate}
                  days={days}
                  label=""
                  level="order"
                  isGhost
                  className="opacity-15"
                />
                {/* 階段條 */}
                <GanttBar
                  startDate={stage.start}
                  endDate={stage.end}
                  timelineStart={startDate}
                  days={days}
                  label={stage.name}
                  progress={stage.progress}
                  group={stage.group}
                  level="stage"
                  tooltipContent={
                    <div className="space-y-1">
                      <div className="font-medium">{stage.name}</div>
                      <div>並行組: G{stage.group}</div>
                      <div>進度: {Math.round(stage.progress * 100)}%</div>
                      <div>部門: {stage.departments.length} 個</div>
                      <div>開始: {new Date(stage.start).toLocaleDateString()}</div>
                      <div>結束: {new Date(stage.end).toLocaleDateString()}</div>
                    </div>
                  }
                />
              </>
            }
          >
            <div className="flex items-center gap-2">
              <Badge className={getGroupBadgeColor(stage.group)}>
                G{stage.group}
              </Badge>
              <div>
                <div className="text-sm font-medium">{stage.name}</div>
                <div className="text-xs text-muted-foreground">
                  {stage.departments.length} 個部門 • {Math.round(stage.progress * 100)}%
                </div>
              </div>
            </div>
          </HierarchyRow>

          {/* 部門列 */}
          {renderDepartments(stage.departments, stage.id, orderId, stage)}
        </div>
      );
    });
  };

  return (
    <div className="space-y-4">
      {/* 麵包屑導航 */}
      {breadcrumb.orderId && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 p-2 rounded">
          <span>導航路徑:</span>
          <Button variant="link" size="sm" onClick={() => setBreadcrumb({})}>
            {breadcrumb.orderId}
          </Button>
          {breadcrumb.stageId && (
            <>
              <span>/</span>
              <Button variant="link" size="sm" onClick={() => setBreadcrumb({ orderId: breadcrumb.orderId })}>
                {breadcrumb.stageId}
              </Button>
            </>
          )}
          {breadcrumb.deptId && (
            <>
              <span>/</span>
              <span>{breadcrumb.deptId}</span>
            </>
          )}
        </div>
      )}

      {/* 甘特圖網格 */}
      <div
        className="relative border rounded-lg overflow-hidden"
        style={{
          minWidth: '1200px',
          display: 'grid',
          gridTemplateColumns: `320px repeat(${days}, minmax(80px, 1fr))`,
          gap: '0'
        }}
      >
        {/* 時間軸標題 */}
        <div className="font-semibold text-sm text-muted-foreground border-r border-b px-4 py-3 flex items-center bg-muted/20">
          訂單 / 階段 / 部門 / 工序
        </div>
        
        {Array.from({length: days}, (_, i) => {
          const date = new Date(startDate);
          date.setDate(date.getDate() + i);
          const isToday = date.toDateString() === new Date().toDateString();
          
          return (
            <div
              key={i}
              className={cn(
                "text-center px-1 py-3 text-xs border-b flex flex-col items-center justify-center",
                i === days - 1 ? '' : 'border-r',
                isToday ? 'bg-primary text-primary-foreground font-semibold' : 'bg-muted/20'
              )}
            >
              <span className="font-medium">
                {date.getMonth() + 1}/{date.getDate()}
              </span>
              <span className="text-xs opacity-75">
                {date.toLocaleDateString('zh-TW', { weekday: 'short' })}
              </span>
            </div>
          );
        })}

        {/* 訂單行 */}
        {orders.map(order => {
          const isOrderExpanded = expanded.orders.has(order.orderId);

          return (
            <div key={order.orderId} className="contents">
              {/* 訂單主列 */}
              <HierarchyRow
                level={0}
                days={days}
                isExpanded={isOrderExpanded}
                onToggle={() => toggleExpanded('orders', order.orderId)}
                timelineContent={
                  <>
                    <GanttBar
                      startDate={order.start}
                      endDate={order.end}
                      timelineStart={startDate}
                      days={days}
                      label={`${order.orderId} - ${order.quantity}件`}
                      progress={order.progress}
                      level="order"
                      tooltipContent={
                        <div className="space-y-1">
                          <div className="font-medium">{order.orderId}</div>
                          <div>{order.customer} - {order.product}</div>
                          <div>數量: {order.quantity}件</div>
                          <div>優先級: {order.priority}</div>
                          <div>整體進度: {Math.round(order.progress * 100)}%</div>
                          <div>開始: {new Date(order.start).toLocaleDateString()}</div>
                          <div>結束: {new Date(order.end).toLocaleDateString()}</div>
                          <div>交期: {new Date(order.dueDate).toLocaleDateString()}</div>
                        </div>
                      }
                    />
                    <DueDateMarker
                      dueDate={order.dueDate}
                      orderEnd={order.end}
                      orderStart={order.start}
                      orderId={order.orderId}
                      progress={order.progress}
                      timelineStart={startDate}
                      days={days}
                      onMarkerClick={onDueMarkerClick}
                    />
                  </>
                }
              >
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <div>
                    <div className="font-medium text-sm">{order.orderId}</div>
                    <div className="text-xs text-muted-foreground">
                      {order.customer} - {order.product} ({order.quantity}件)
                    </div>
                  </div>
                  <Badge 
                    variant={order.priority === "high" ? "destructive" : order.priority === "medium" ? "default" : "secondary"} 
                    className="text-xs ml-auto"
                  >
                    {order.priority}
                  </Badge>
                </div>
              </HierarchyRow>

              {/* 階段行 */}
              {renderStages(order.stages, order.orderId, order)}
            </div>
          );
        })}
      </div>
    </div>
  );
}